/**
 * Task Module API - 统一入口
 * @description 所有 Task 相关 API 的统一导出模块
 *              包括：公开任务查询、任务管理（CRUD）、承接管理
 *              RESTful: 统一使用 /tasks (复数) 路径规范
 */

import type { Bid } from '@/types/bid'
import type {
  BidForm,
  PageResult,
  Task,
  TaskDetail,
  TaskItem,
  TaskListParams,
  TaskListResponse,
  TaskPublishForm,
  TaskQueryParams,
  TaskSearchParams,
} from '@/types/task'
import { httpClient } from '@/utils/request'

// ==================== 公开任务 API ====================

/**
 * 获取任务列表（含分类/筛选/排序/分页）
 * RESTful: GET /tasks
 */
export function getTaskCategories(params: TaskListParams) {
  return httpClient.get<PageResult<TaskItem>>('/tasks', { params })
}

/**
 * 获取任务详情
 * RESTful: GET /tasks/{id}
 */
export async function getTaskDetail(id: string | number): Promise<TaskDetail> {
  const res = await httpClient.get<any>(`/tasks/${String(id)}`)
  // 适配后端 Sprint L2 M8 结构 { task, publisher, attachments, milestones, bids, bid_count }
  if (res && res.task) {
    const status = res.task.status?.toUpperCase()
    return {
      ...res.task,
      status,
      publisher: res.publisher || res.task.publisher,
      attachments: res.attachments || res.task.attachments,
      milestones: res.milestones || res.task.milestones,
      bids: res.bids || res.task.bids,
      bid_count: res.bid_count !== undefined ? res.bid_count : res.task.bid_count,
    } as TaskDetail
  }
  if (res && res.status) {
    res.status = res.status.toUpperCase()
  }
  return res as TaskDetail
}

/**
 * 搜索任务
 * RESTful: GET /tasks/search
 */
export function searchTasks(params: TaskSearchParams) {
  return httpClient.get<PageResult<TaskItem>>('/tasks/search', { params })
}

/**
 * 获取最新任务
 * RESTful: GET /tasks/latest
 */
export function getLatestTasks(params?: { limit?: number }) {
  return httpClient.get<TaskItem[]>('/tasks/latest', { params })
}

// ==================== 任务管理 API ====================

/**
 * 发布任务
 * RESTful: POST /tasks
 */
export function publishTask(data: TaskPublishForm) {
  return httpClient.post<void>('/tasks', data)
}

/**
 * 更新任务（草稿/被拒绝状态可编辑）
 * RESTful: PUT /tasks/{id}
 */
export function updateTask(id: string | number, data: Partial<TaskPublishForm> | Partial<Task>) {
  return httpClient.put<Task>(`/tasks/${String(id)}`, data)
}

/**
 * 删除任务
 * RESTful: DELETE /tasks/{id}
 */
export function deleteTask(id: string | number) {
  return httpClient.delete<void>(`/tasks/${String(id)}`)
}

/**
 * 重新提交任务审核
 * 被拒绝的任务修改后重新提交（仅触发状态重置，不接受 body）
 * RESTful: POST /tasks/{id}/actions/resubmit
 */
export function resubmitTask(id: string | number) {
  return httpClient.post<Task>(`/tasks/${id}/actions/resubmit`)
}

/**
 * 终止任务
 * RESTful: POST /tasks/{id}/actions/terminate
 */
export function terminateTask(id: string | number) {
  return httpClient.post(`/tasks/${id}/actions/terminate`)
}

/**
 * 添加任务附件
 * RESTful: POST /tasks/{id}/attachments
 */
export function createTaskAttachment(id: string | number, data: { file_name: string, file_url: string, file_size: number, file_type?: string }) {
  return httpClient.post<any>(`/tasks/${id}/attachments`, data)
}

/**
 * 删除任务附件
 * RESTful: DELETE /tasks/{id}/attachments/{attachment_id}
 */
export function deleteTaskAttachment(id: string | number, attachmentId: string) {
  return httpClient.delete<void>(`/tasks/${id}/attachments/${attachmentId}`)
}
/**
 * 支付任务托管金（PENDING_PAYMENT → BIDDING）
 * RESTful: POST /tasks/{id}/escrow-payment
 */
export function payEscrowFund(id: string | number) {
  return httpClient.post(`/tasks/${id}/escrow-payment`)
}

// ==================== 投标 API ====================

/**
 * 投标
 * RESTful: POST /tasks/{taskId}/bids
 */
export function bidTask(taskId: string | number, data: BidForm) {
  return httpClient.post<void>(`/tasks/${taskId}/bids`, data)
}

/**
 * 获取任务投标列表
 * RESTful: GET /tasks/{id}/bids
 */
export function getBids(id: string | number) {
  return httpClient.get<Bid[]>(`/tasks/${id}/bids`)
}

/**
 * 选标（雇主选定服务商）
 * RESTful: POST /tasks/{id}/bids/{bidId}/select
 */
export function selectBid(id: string | number, bidId: string | number) {
  return httpClient.post(`/tasks/${id}/bids/${bidId}/select`)
}

// ==================== 承接管理 API ====================

/**
 * 获取承接管理列表
 * 服务商查看自己承接的所有任务
 * RESTful: GET /users/me/tasks/undertaken
 */
export function getUndertakenTasks(params: TaskQueryParams) {
  return httpClient.get<TaskListResponse>('/users/me/tasks/undertaken', { params })
}
